import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FaMagic, FaMoon, FaPalette } from "react-icons/fa";
import { useIntl } from "react-intl";
import { useAppearance } from "utils/appearance/AppearanceProvider";
import type { AppearanceMode, AppearanceStyle } from "utils/appearance/cookies";
import { radioSx, switchSx } from "utils/chackra/controlStyles";

/**
 * The appearance control: colour mode (Light / Dark) and style (Calm /
 * Expressive). Replaces the old light/dark ColorModeSwitcher everywhere.
 *
 * Spec: specs/active/2026-08-29-website-design-system-adoption (Phase 2, Rev 3).
 *
 *  - `inline`  — two radio groups, for the mobile drawer.
 *  - `toggles` — two compact switches (Dark, Expressive) that sit on one row
 *                with the language picker in the header popovers.
 *  - `menu`   — one icon button opening a menu with two radio groups; the
 *               always-visible desktop entry point, and the compact control on
 *               routes that render no header. No Portal: the menu stays inside
 *               the header's stacking context.
 *
 * Either way the accessible name states the current selection, e.g.
 * "Appearance: Dark, Expressive" (localised).
 */

type Variant = "inline" | "menu" | "toggles";

/** Re-selecting the option that is already shown is still a choice: on a
 *  dark OS with no cookie, clicking "Dark" must pin dark. The group onChange
 *  does not fire for an unchanged value, so the option's own click does it
 *  (on the Radio's <input> — a Radio's own onClick lands on the decorative
 *  control span, which a label click never reaches). */
const pinIfCurrent =
  <T extends string>(current: T, value: T, set: (v: T) => void) =>
  () => {
    if (value === current) set(value);
  };

const useLabels = () => {
  const intl = useIntl();
  const { mode, style } = useAppearance();
  const t = (id: string) => intl.formatMessage({ id });
  const modeText = { light: t("ModeLight"), dark: t("ModeDark") };
  const styleText = { calm: t("StyleCalm"), expressive: t("StyleExpressive") };
  return {
    modeText,
    styleText,
    modeGroup: t("AppearanceModeLabel"),
    styleGroup: t("AppearanceStyleLabel"),
    summary: `${t("AppearanceLabel")}: ${modeText[mode]}, ${styleText[style]}`,
  };
};

const InlineAppearanceControl = () => {
  const { mode, style, setMode, setStyle } = useAppearance();
  const { modeText, styleText, modeGroup, styleGroup, summary } = useLabels();

  return (
    <Box role="group" aria-label={summary}>
      <Stack spacing={2}>
        <RadioGroup
          aria-label={modeGroup}
          value={mode}
          onChange={(next) => setMode(next as AppearanceMode)}
        >
          <HStack spacing={4}>
            <Radio
              sx={radioSx}
              value="light"
              inputProps={{
                onClick: pinIfCurrent<AppearanceMode>(mode, "light", setMode),
              }}
            >
              {modeText.light}
            </Radio>
            <Radio
              sx={radioSx}
              value="dark"
              inputProps={{
                onClick: pinIfCurrent<AppearanceMode>(mode, "dark", setMode),
              }}
            >
              {modeText.dark}
            </Radio>
          </HStack>
        </RadioGroup>
        <RadioGroup
          aria-label={styleGroup}
          value={style}
          onChange={(next) => setStyle(next as AppearanceStyle)}
        >
          <HStack spacing={4}>
            <Radio
              sx={radioSx}
              value="calm"
              inputProps={{
                onClick: pinIfCurrent<AppearanceStyle>(style, "calm", setStyle),
              }}
            >
              {styleText.calm}
            </Radio>
            <Radio
              sx={radioSx}
              value="expressive"
              inputProps={{
                onClick: pinIfCurrent<AppearanceStyle>(style, "expressive", setStyle),
              }}
            >
              {styleText.expressive}
            </Radio>
          </HStack>
        </RadioGroup>
      </Stack>
    </Box>
  );
};

const MenuAppearanceControl = () => {
  const { mode, style, setMode, setStyle } = useAppearance();
  const { modeText, styleText, modeGroup, styleGroup, summary } = useLabels();

  return (
    <Menu closeOnSelect={false} placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label={summary}
        title={summary}
        icon={<FaPalette />}
        variant="ghost"
        color="text.primary"
      />
      <MenuList minW="48" zIndex="dropdown">
        <MenuOptionGroup
          title={modeGroup}
          type="radio"
          value={mode}
          onChange={(next) => setMode(next as AppearanceMode)}
        >
          <MenuItemOption
            value="light"
            onClick={pinIfCurrent<AppearanceMode>(mode, "light", setMode)}
          >
            {modeText.light}
          </MenuItemOption>
          <MenuItemOption
            value="dark"
            onClick={pinIfCurrent<AppearanceMode>(mode, "dark", setMode)}
          >
            {modeText.dark}
          </MenuItemOption>
        </MenuOptionGroup>
        <MenuDivider />
        <MenuOptionGroup
          title={styleGroup}
          type="radio"
          value={style}
          onChange={(next) => setStyle(next as AppearanceStyle)}
        >
          <MenuItemOption
            value="calm"
            onClick={pinIfCurrent<AppearanceStyle>(style, "calm", setStyle)}
          >
            {styleText.calm}
          </MenuItemOption>
          <MenuItemOption
            value="expressive"
            onClick={pinIfCurrent<AppearanceStyle>(style, "expressive", setStyle)}
          >
            {styleText.expressive}
          </MenuItemOption>
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};

/** Two switches: on = Dark / Expressive, off = Light / Calm. A toggle is
 *  always a change, so every flip is an explicit choice and persists. Each
 *  switch names its own state ("Dark" on, "Light" off) for assistive tech. */
const ToggleAppearanceControl = () => {
  const { mode, style, setMode, setStyle } = useAppearance();
  const { modeText, styleText, summary } = useLabels();
  const isDark = mode === "dark";
  const isExpressive = style === "expressive";
  const item = (
    icon: typeof FaMoon,
    label: string,
    checked: boolean,
    onToggle: (on: boolean) => void
  ) => (
    <Tooltip label={label} hasArrow openDelay={300}>
      <HStack as="label" spacing={1} cursor="pointer" color="text.secondary">
        <Box as={icon} boxSize={3} aria-hidden="true" />
        <Switch
          size="sm"
          sx={switchSx}
          isChecked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={label}
        />
      </HStack>
    </Tooltip>
  );
  return (
    <HStack spacing={2} role="group" aria-label={summary} title={summary}>
      {item(FaMoon, isDark ? modeText.dark : modeText.light, isDark, (on) =>
        setMode(on ? "dark" : "light")
      )}
      {item(FaMagic, isExpressive ? styleText.expressive : styleText.calm, isExpressive, (on) =>
        setStyle(on ? "expressive" : "calm")
      )}
    </HStack>
  );
};

export const AppearanceControl = ({ variant = "inline" }: { variant?: Variant }) => {
  if (variant === "menu") return <MenuAppearanceControl />;
  if (variant === "toggles") return <ToggleAppearanceControl />;
  return <InlineAppearanceControl />;
};

/** Visible caption for the inline variant where a surrounding layout wants one. */
export const AppearanceCaption = () => {
  const intl = useIntl();
  return (
    <Text fontSize="sm" color="text.secondary" fontWeight="medium">
      {intl.formatMessage({ id: "AppearanceLabel" })}
    </Text>
  );
};

export default AppearanceControl;
